import React, { createContext, useContext, useState } from 'react';

const DocumentationContext = createContext();

export const useDocumentation = () => {
  const context = useContext(DocumentationContext);
  if (!context) {
    throw new Error('useDocumentation must be used within DocumentationProvider');
  }
  return context;
};

export const DocumentationProvider = ({ children }) => {
  const [activeSection, setActiveSection] = useState('getting-started');
  const [activeTopic, setActiveTopic] = useState('introduction');

  const documentationData = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      topics: [
        {
          id: 'introduction',
          title: 'Introduction',
          content: `
            <h1>Welcome to Owlex Documentation</h1>
            <p>Owlex is a cutting-edge Business Intelligence tool that empowers you to transform data into actionable insights.</p>
            
            <h2>What is Owlex?</h2>
            <p>Owlex provides a comprehensive suite of tools for data analysis, visualization, and reporting. Whether you're a data analyst, business user, or developer, Owlex has the features you need.</p>
            
            <h2>Key Features</h2>
            <ul>
              <li>AI-Powered data insights</li>
              <li>Multiple database connections</li>
              <li>Semantic Layer for fast queries</li>
              <li>Real-time notifications</li>
              <li>Collaborative dashboards</li>
            </ul>
          `
        },
        {
          id: 'installation',
          title: 'Installation',
          content: `
            <h1>Installation Guide</h1>
            <p>Getting started with Owlex is quick and easy. Follow these comprehensive steps to set up your environment and start analyzing your data in minutes.</p>
            
            <h2>System Requirements</h2>
            <p>Before you begin the installation process, ensure your system meets the following minimum requirements:</p>
            <ul>
              <li>Modern web browser (Chrome 90+, Firefox 88+, Safari 14+, or Edge 90+)</li>
              <li>Stable internet connection with minimum 5 Mbps speed</li>
              <li>Minimum 4GB RAM (8GB recommended for optimal performance)</li>
              <li>Screen resolution of at least 1280x720 pixels</li>
              <li>JavaScript enabled in your browser</li>
              <li>Cookies and local storage enabled</li>
            </ul>
            
            <h2>Supported Operating Systems</h2>
            <p>Owlex is a cloud-based platform that works seamlessly across all major operating systems:</p>
            <ul>
              <li>Windows 10 or later</li>
              <li>macOS 10.14 (Mojave) or later</li>
              <li>Linux (Ubuntu 18.04+, Fedora 32+, or equivalent)</li>
              <li>Chrome OS</li>
            </ul>
            
            <h2>Pre-Installation Checklist</h2>
            <p>Before starting the installation process, make sure you have the following information ready:</p>
            <ul>
              <li>Valid email address for account creation</li>
              <li>Company or organization name</li>
              <li>Database connection credentials (if applicable)</li>
              <li>User role and permission requirements</li>
              <li>Expected number of users in your organization</li>
            </ul>
            
            <h2>Step 1: Account Creation</h2>
            <p>To begin using Owlex, you'll first need to create an account. Visit our website and click on the "Sign Up" button. You'll be presented with a registration form where you need to provide:</p>
            <ul>
              <li>Full name</li>
              <li>Email address</li>
              <li>Secure password (minimum 8 characters with uppercase, lowercase, and numbers)</li>
              <li>Company name</li>
              <li>Industry sector</li>
            </ul>
            <p>After filling in all required fields, accept our Terms of Service and Privacy Policy, then click "Create Account".</p>
            
            <h2>Step 2: Email Verification</h2>
            <p>Once you've submitted your registration, we'll send a verification email to the address you provided. This is an important security step to ensure the validity of your account.</p>
            <p>Check your inbox for an email from noreply@owlex.com with the subject "Verify Your Owlex Account". Click the verification link within 24 hours to activate your account. If you don't see the email, check your spam or junk folder.</p>
            <p>If you haven't received the verification email after 10 minutes, you can request a new one by clicking the "Resend Verification Email" button on the login page.</p>
            
            <h2>Step 3: Initial Login and Profile Setup</h2>
            <p>After verifying your email, return to the Owlex login page and sign in using your credentials. Upon your first login, you'll be guided through an initial setup wizard that helps you:</p>
            <ul>
              <li>Complete your user profile</li>
              <li>Set up your organization workspace</li>
              <li>Configure notification preferences</li>
              <li>Choose your timezone and language settings</li>
              <li>Customize your dashboard layout</li>
            </ul>
            
            <h2>Step 4: Organization Configuration</h2>
            <p>The next step involves setting up your organization's structure within Owlex. This includes:</p>
            <ul>
              <li>Defining user roles and permissions</li>
              <li>Creating teams and departments</li>
              <li>Setting up approval workflows</li>
              <li>Configuring data access policies</li>
              <li>Establishing naming conventions for dashboards and reports</li>
            </ul>
            <p>This configuration can be adjusted later, but setting it up properly from the start ensures a smooth experience for all team members.</p>
            
            <h2>Step 5: Data Source Connection</h2>
            <p>Now it's time to connect your first data source. Owlex supports a wide variety of databases and data warehouses. Navigate to Settings > Data Sources and click "Add New Connection". You'll need to provide:</p>
            <ul>
              <li>Database type (PostgreSQL, MySQL, Oracle, etc.)</li>
              <li>Host address or server name</li>
              <li>Port number</li>
              <li>Database name</li>
              <li>Username and password with appropriate permissions</li>
              <li>SSL/TLS configuration (if required)</li>
            </ul>
            <p>Once you've entered all connection details, click "Test Connection" to verify that Owlex can successfully communicate with your database.</p>
            
            <h2>Step 6: Security Configuration</h2>
            <p>Security is paramount when dealing with business data. Configure the following security settings:</p>
            <ul>
              <li>Enable two-factor authentication (2FA) for all users</li>
              <li>Set up IP whitelisting if your organization requires it</li>
              <li>Configure session timeout duration</li>
              <li>Set up audit logging</li>
              <li>Define data encryption policies</li>
              <li>Configure backup and disaster recovery options</li>
            </ul>
            
            <h2>Step 7: First Dashboard Creation</h2>
            <p>With your data source connected, you're ready to create your first dashboard. Click on "New Dashboard" and start by:</p>
            <ul>
              <li>Giving your dashboard a descriptive name</li>
              <li>Adding a description and tags for easy discovery</li>
              <li>Selecting the data source to query</li>
              <li>Adding your first visualization</li>
              <li>Configuring filters and parameters</li>
            </ul>
            
            <h2>Step 8: Team Onboarding</h2>
            <p>Once your initial setup is complete, you can invite team members to join your Owlex workspace. Go to Settings > Team Members and click "Invite Users". You can:</p>
            <ul>
              <li>Send individual email invitations</li>
              <li>Bulk upload users via CSV file</li>
              <li>Configure SSO (Single Sign-On) for enterprise accounts</li>
              <li>Assign roles and permissions to each user</li>
              <li>Set up user groups for easier permission management</li>
            </ul>
            
            <h2>Troubleshooting Common Installation Issues</h2>
            <p>If you encounter any issues during installation, here are some common problems and their solutions:</p>
            
            <h2>Issue: Verification Email Not Received</h2>
            <p>If you haven't received the verification email within 10 minutes, try these steps:</p>
            <ul>
              <li>Check your spam or junk folder</li>
              <li>Verify that you entered the correct email address</li>
              <li>Add noreply@owlex.com to your email whitelist</li>
              <li>Request a new verification email from the login page</li>
            </ul>
            
            <h2>Issue: Database Connection Fails</h2>
            <p>If you're unable to connect to your database, verify:</p>
            <ul>
              <li>All connection credentials are correct</li>
              <li>The database server is accessible from the internet</li>
              <li>Firewall rules allow connections from Owlex IP addresses</li>
              <li>SSL/TLS settings match your database configuration</li>
              <li>The database user has appropriate permissions</li>
            </ul>
            
            <h2>Post-Installation Best Practices</h2>
            <p>After completing the installation, follow these best practices to ensure optimal performance:</p>
            <ul>
              <li>Regularly review and update user permissions</li>
              <li>Set up automated backups for critical dashboards</li>
              <li>Configure monitoring and alerting for important metrics</li>
              <li>Establish a naming convention for dashboards and reports</li>
              <li>Document your data models and semantic layer</li>
              <li>Schedule regular team training sessions</li>
              <li>Keep track of new Owlex features and updates</li>
            </ul>
            
            <h2>Next Steps</h2>
            <p>Congratulations! You've successfully completed the Owlex installation and setup process. Now you're ready to:</p>
            <ul>
              <li>Explore advanced features and capabilities</li>
              <li>Build comprehensive dashboards for your team</li>
              <li>Set up automated reports and alerts</li>
              <li>Integrate with other tools in your data stack</li>
              <li>Leverage AI-powered insights with Owlex AI Bot</li>
            </ul>
            <p>For more detailed information on each of these topics, continue exploring our documentation sections.</p>
          `
        },
        {
          id: 'quick-start',
          title: 'Quick Start Guide',
          content: `
            <h1>Quick Start Guide</h1>
            <p>Get up and running with Owlex in minutes.</p>
            
            <h2>Step 1: Create Your First Dashboard</h2>
            <p>Navigate to the Dashboards section and click "New Dashboard".</p>
            
            <h2>Step 2: Add Visualizations</h2>
            <p>Choose from various chart types and data sources to create your visualizations.</p>
            
            <h2>Step 3: Share Your Work</h2>
            <p>Collaborate with your team by sharing dashboards and reports.</p>
          `
        }
      ]
    },
    {
      id: 'data-sources',
      title: 'Data Sources',
      topics: [
        {
          id: 'connecting-databases',
          title: 'Connecting Databases',
          content: `
            <h1>Connecting Databases</h1>
            <p>Owlex supports multiple database types for seamless data integration.</p>
            
            <h2>Supported Databases</h2>
            <ul>
              <li>PostgreSQL</li>
              <li>MySQL</li>
              <li>Oracle</li>
              <li>Microsoft SQL Server</li>
              <li>MongoDB</li>
            </ul>
            
            <h2>Connection Setup</h2>
            <p>To connect a database, navigate to Settings > Data Sources and provide your connection credentials.</p>
          `
        },
        {
          id: 'data-security',
          title: 'Data Security',
          content: `
            <h1>Data Security</h1>
            <p>Your data security is our top priority.</p>
            
            <h2>Security Features</h2>
            <ul>
              <li>End-to-end encryption</li>
              <li>Role-based access control</li>
              <li>Audit logging</li>
              <li>Compliance certifications</li>
            </ul>
          `
        }
      ]
    },
    {
      id: 'dashboards',
      title: 'Dashboards',
      topics: [
        {
          id: 'creating-dashboards',
          title: 'Creating Dashboards',
          content: `
            <h1>Creating Dashboards</h1>
            <p>Learn how to create stunning, interactive dashboards.</p>
            
            <h2>Dashboard Basics</h2>
            <p>Dashboards are collections of visualizations that provide insights at a glance.</p>
            
            <h2>Best Practices</h2>
            <ul>
              <li>Keep it simple and focused</li>
              <li>Use appropriate chart types</li>
              <li>Add filters for interactivity</li>
              <li>Organize with tabs and sections</li>
            </ul>
          `
        },
        {
          id: 'sharing-dashboards',
          title: 'Sharing Dashboards',
          content: `
            <h1>Sharing Dashboards</h1>
            <p>Collaborate with your team by sharing dashboards.</p>
            
            <h2>Sharing Options</h2>
            <ul>
              <li>Public links</li>
              <li>Team sharing</li>
              <li>Email scheduling</li>
              <li>Embedded dashboards</li>
            </ul>
          `
        }
      ]
    },
    {
      id: 'ai-features',
      title: 'AI Features',
      topics: [
        {
          id: 'owlex-ai-bot',
          title: 'Owlex AI Bot',
          content: `
            <h1>Owlex AI Bot</h1>
            <p>Leverage AI-powered insights with the Owlex AI Bot.</p>
            
            <h2>What Can AI Bot Do?</h2>
            <ul>
              <li>Natural language queries</li>
              <li>Automated insights discovery</li>
              <li>Anomaly detection</li>
              <li>Predictive analytics</li>
            </ul>
            
            <h2>Using AI Bot</h2>
            <p>Simply type your question in natural language, and the AI Bot will generate relevant visualizations and insights.</p>
          `
        },
        {
          id: 'ai-recommendations',
          title: 'AI Recommendations',
          content: `
            <h1>AI Recommendations</h1>
            <p>Get smart recommendations for your data analysis.</p>
            
            <h2>Features</h2>
            <ul>
              <li>Chart type suggestions</li>
              <li>Data quality alerts</li>
              <li>Performance optimization tips</li>
            </ul>
          `
        }
      ]
    },
    {
      id: 'api-reference',
      title: 'API Reference',
      topics: [
        {
          id: 'rest-api',
          title: 'REST API',
          content: `
            <h1>REST API</h1>
            <p>Integrate Owlex into your applications using our REST API.</p>
            
            <h2>Authentication</h2>
            <p>All API requests require authentication using API keys.</p>
            
            <h2>Endpoints</h2>
            <ul>
              <li>GET /api/dashboards</li>
              <li>POST /api/queries</li>
              <li>PUT /api/users</li>
            </ul>
          `
        },
        {
          id: 'webhooks',
          title: 'Webhooks',
          content: `
            <h1>Webhooks</h1>
            <p>Set up webhooks to receive real-time notifications.</p>
            
            <h2>Webhook Events</h2>
            <ul>
              <li>Dashboard created</li>
              <li>Query executed</li>
              <li>Alert triggered</li>
            </ul>
          `
        }
      ]
    }
  ];

  // Helper function to get current topic
  const getCurrentTopic = () => {
    const section = documentationData.find(s => s.id === activeSection);
    if (!section) return null;
    return section.topics.find(t => t.id === activeTopic);
  };

  // Helper function to get previous/next topics
  const getNavigationTopics = () => {
    let allTopics = [];
    documentationData.forEach(section => {
      section.topics.forEach(topic => {
        allTopics.push({
          ...topic,
          sectionId: section.id,
          sectionTitle: section.title
        });
      });
    });

    const currentIndex = allTopics.findIndex(
      t => t.id === activeTopic && t.sectionId === activeSection
    );

    return {
      previous: currentIndex > 0 ? allTopics[currentIndex - 1] : null,
      next: currentIndex < allTopics.length - 1 ? allTopics[currentIndex + 1] : null
    };
  };

  const value = {
    documentationData,
    activeSection,
    setActiveSection,
    activeTopic,
    setActiveTopic,
    getCurrentTopic,
    getNavigationTopics
  };

  return (
    <DocumentationContext.Provider value={value}>
      {children}
    </DocumentationContext.Provider>
  );
};

export default DocumentationContext;

